import type { AbiFunction } from "./types";
import type { KnownAddress } from "./useKnownAddresses";

// ---------------------------------------------------------------------------
// ABI extraction
// ---------------------------------------------------------------------------

export function extractFunctions(abi: readonly unknown[]): AbiFunction[] {
  return (abi as { type: string }[]).filter(
    (item) => item.type === "function",
  ) as unknown as AbiFunction[];
}

export function isViewFunction(fn: AbiFunction): boolean {
  return fn.stateMutability === "view" || fn.stateMutability === "pure";
}

export function isPayableFunction(fn: AbiFunction): boolean {
  return fn.stateMutability === "payable";
}

/** Unique key for a function — handles overloaded names by appending input types */
export function fnSigKey(fn: AbiFunction): string {
  return `${fn.name}(${fn.inputs.map((i) => i.type).join(",")})`;
}

// ---------------------------------------------------------------------------
// Error parsing — strip viem internals, return human-readable message
// ---------------------------------------------------------------------------

export function parseViemError(err: unknown): {
  message: string;
  details: string | null;
} {
  const raw = err instanceof Error ? err.message : String(err);

  // Strip "Version: viem@X.Y.Z" lines
  const stripped = raw
    .replace(/Version: viem@[\d.]+/g, "")
    .replace(/Docs: https?:\/\/[^\s]+/g, "")
    .replace(/\n{2,}/g, "\n")
    .trim();

  // Try to extract the first meaningful line
  const lines = stripped.split("\n").filter((l) => l.trim());
  const firstLine = lines[0] ?? stripped;

  // Common error patterns with human-readable rewrites
  if (/invalid address/i.test(firstLine)) {
    return {
      message: "Invalid address — enter a valid 0x address (40 hex characters)",
      details: stripped,
    };
  }
  if (/execution reverted/i.test(firstLine)) {
    // Try to extract the reason after "reverted"
    const reasonMatch =
      /execution reverted[:\s]*(?:with reason[:\s]*)?"?([^"]+)"?/i.exec(
        stripped,
      );
    return {
      message: reasonMatch?.[1]
        ? `Reverted: ${reasonMatch[1].trim()}`
        : "Transaction reverted",
      details: stripped,
    };
  }
  if (
    /could not coalesce/i.test(firstLine) ||
    /invalid.*bigint/i.test(firstLine)
  ) {
    return {
      message: "Invalid number — enter a valid integer value",
      details: stripped,
    };
  }
  if (/overflow/i.test(firstLine)) {
    return {
      message: "Number too large for this parameter type",
      details: stripped,
    };
  }

  return {
    message:
      firstLine.length > 120 ? firstLine.slice(0, 120) + "..." : firstLine,
    details: lines.length > 1 ? stripped : null,
  };
}

// ---------------------------------------------------------------------------
// Result formatting
// ---------------------------------------------------------------------------

export function formatContractResult(
  decoded: unknown,
  outputTypes?: { name: string; type: string }[],
): string {
  if (decoded === undefined || decoded === null) return "(empty)";

  // Single bigint — format with commas
  if (typeof decoded === "bigint") {
    return decoded.toLocaleString();
  }

  // Single string — strip surrounding quotes, show empty string indicator
  if (typeof decoded === "string") {
    if (decoded === "") return "";
    return decoded;
  }

  // Boolean
  if (typeof decoded === "boolean") {
    return String(decoded);
  }

  // Array (tuple result) — format each element
  if (Array.isArray(decoded)) {
    if (decoded.length === 1) {
      return formatContractResult(decoded[0], outputTypes?.slice(0, 1));
    }
    const lines = decoded.map((v, i) => {
      const name = outputTypes?.[i]?.name;
      const label = name ? `${name}: ` : `[${String(i)}]: `;
      const formatted =
        typeof v === "bigint"
          ? v.toLocaleString()
          : typeof v === "string" && v === ""
            ? '""'
            : String(v);
      return `${label}${formatted}`;
    });
    return lines.join("\n");
  }

  // Fallback: JSON with bigint handling
  return JSON.stringify(
    decoded,
    (_k, v) => (typeof v === "bigint" ? v.toString() : (v as unknown)),
    2,
  );
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

export function validateInput(
  value: string,
  solidityType: string,
): { valid: boolean; error?: string } {
  if (!value) return { valid: true }; // empty is neutral

  // Address
  if (solidityType === "address") {
    if (!/^0x[0-9a-fA-F]{40}$/.test(value)) {
      return { valid: false, error: "Must be 0x + 40 hex characters" };
    }
    return { valid: true };
  }

  // Bool
  if (solidityType === "bool") {
    if (value !== "true" && value !== "false") {
      return { valid: false, error: 'Must be "true" or "false"' };
    }
    return { valid: true };
  }

  // Bytes
  if (solidityType === "bytes" || /^bytes\d+$/.test(solidityType)) {
    if (!/^0x[0-9a-fA-F]*$/.test(value)) {
      return { valid: false, error: "Must be valid hex (0x...)" };
    }
    if (solidityType !== "bytes") {
      const size = parseInt(solidityType.replace("bytes", ""), 10);
      const expectedLen = 2 + size * 2; // "0x" + hex chars
      if (value.length !== expectedLen) {
        return {
          valid: false,
          error: `Must be exactly ${String(size)} bytes (${String(expectedLen)} chars including 0x)`,
        };
      }
    }
    return { valid: true };
  }

  // Uint/Int
  if (/^u?int\d*$/.test(solidityType)) {
    try {
      const n = BigInt(value);
      const isUnsigned = solidityType.startsWith("u");
      if (isUnsigned && n < 0n) {
        return { valid: false, error: "Must be non-negative" };
      }
      const bits = parseInt(solidityType.replace(/^u?int/, "") || "256", 10);
      const max = isUnsigned
        ? (1n << BigInt(bits)) - 1n
        : (1n << BigInt(bits - 1)) - 1n;
      const min = isUnsigned ? 0n : -(1n << BigInt(bits - 1));
      if (n > max || n < min) {
        return { valid: false, error: `Out of range for ${solidityType}` };
      }
      return { valid: true };
    } catch {
      return { valid: false, error: "Must be a valid integer" };
    }
  }

  // String — always valid
  if (solidityType === "string") {
    return { valid: true };
  }

  return { valid: true };
}

// ---------------------------------------------------------------------------
// Capitalize param name for label
// ---------------------------------------------------------------------------

export function capitalizeParamName(name: string): string {
  if (!name) return "Input";
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// ---------------------------------------------------------------------------
// Example parameter suggestions
// ---------------------------------------------------------------------------

export interface ExampleSuggestion {
  params: Record<string, string>;
  label: string;
}

const MAX_UINT256 =
  "115792089237316195423570985008687907853269984665640564039457584007913129639935";
const ONE_ETH = "1000000000000000000";
const ZERO_BYTES32 =
  "0x0000000000000000000000000000000000000000000000000000000000000000";

/** Find an address by label prefix match from the known addresses list. */
function findAddr(
  list: KnownAddress[],
  ...labels: string[]
): string | undefined {
  for (const label of labels) {
    const found = list.find(
      (a) => a.label === label || a.label.startsWith(label),
    );
    if (found) return found.address;
  }
  return undefined;
}

/**
 * Generate a practical example suggestion for a contract function.
 * Returns null when no meaningful example can be generated.
 */
export function getExampleSuggestion(
  abiKey: string,
  fn: AbiFunction,
  knownAddresses: KnownAddress[],
): ExampleSuggestion | null {
  if (fn.inputs.length === 0) return null;

  const alice = findAddr(knownAddresses, "Account 1", "Alice");
  const bob = findAddr(knownAddresses, "Account 2", "Bob");
  const weth = findAddr(knownAddresses, "WETH");
  const usdc = findAddr(knownAddresses, "USDC");
  const v2Router = findAddr(knownAddresses, "V2 Router");
  const v4PM = findAddr(knownAddresses, "V4 PositionMgr", "V4 Position");

  // --- Contract-specific examples ---

  // ERC-20 tokens
  if (abiKey === "MockERC20" || abiKey === "WETH9") {
    if (fn.name === "balanceOf" && alice) {
      return {
        params: { owner: alice, account: alice },
        label: "Alice's token balance",
      };
    }
    if (fn.name === "allowance" && alice && v2Router) {
      return {
        params: { owner: alice, spender: v2Router },
        label: "Alice \u2192 V2 Router allowance",
      };
    }
    if (fn.name === "approve" && v2Router) {
      return {
        params: { spender: v2Router, amount: MAX_UINT256, value: MAX_UINT256 },
        label: "Approve V2 Router (max)",
      };
    }
    if (fn.name === "transfer" && bob) {
      return {
        params: { to: bob, dst: bob, amount: ONE_ETH, value: ONE_ETH },
        label: "Send 1 token to Bob",
      };
    }
  }

  // V2 Factory
  if (abiKey === "V2Factory") {
    if (fn.name === "getPair" && weth && usdc) {
      return {
        params: { tokenA: weth, tokenB: usdc },
        label: "WETH/USDC pair address",
      };
    }
  }

  // V2 Router
  if (abiKey === "V2Router") {
    if (fn.name === "getAmountsOut" && weth && usdc) {
      return {
        params: { amountIn: ONE_ETH, path: `[${weth},${usdc}]` },
        label: "1 WETH \u2192 USDC quote",
      };
    }
    if (fn.name === "getAmountsIn" && weth && usdc) {
      return {
        params: { amountOut: "1000000", path: `[${weth},${usdc}]` },
        label: "WETH needed for 1 USDC",
      };
    }
  }

  // V3 Factory
  if (abiKey === "V3Factory") {
    if (fn.name === "getPool" && weth && usdc) {
      return {
        params: { tokenA: weth, tokenB: usdc, fee: "3000" },
        label: "WETH/USDC 0.3% pool",
      };
    }
  }

  // V3 Position Manager
  if (abiKey === "V3PositionManager") {
    if (fn.name === "positions") {
      return { params: { tokenId: "1" }, label: "First LP position details" };
    }
    if (fn.name === "balanceOf" && alice) {
      return { params: { owner: alice }, label: "Alice's LP count" };
    }
    if (fn.name === "ownerOf") {
      return { params: { tokenId: "1" }, label: "Owner of position #1" };
    }
    if (fn.name === "tokenOfOwnerByIndex" && alice) {
      return {
        params: { owner: alice, index: "0" },
        label: "Alice's 1st position ID",
      };
    }
  }

  // V3 Quoter
  if (abiKey === "V3QuoterV2") {
    if (fn.name === "quoteExactInputSingle" && weth && usdc) {
      return {
        params: {
          tokenIn: weth,
          tokenOut: usdc,
          fee: "3000",
          amountIn: ONE_ETH,
          sqrtPriceLimitX96: "0",
        },
        label: "Quote 1 WETH \u2192 USDC (0.3%)",
      };
    }
  }

  // V4 PoolManager
  if (abiKey === "PoolManager") {
    if (fn.name === "balanceOf" && alice) {
      return {
        params: { owner: alice, id: "0" },
        label: "Alice's currency-0 balance",
      };
    }
    if (fn.name === "totalSupply") {
      return { params: { id: "0" }, label: "Supply for currency 0" };
    }
    if (fn.name === "isOperator" && alice && v4PM) {
      return {
        params: { owner: alice, operator: v4PM },
        label: "V4 PM is Alice's operator?",
      };
    }
    if (fn.name === "protocolFeesAccrued" && weth) {
      return {
        params: { currency: weth },
        label: "WETH protocol fees accrued",
      };
    }
    if (fn.name === "allowance" && alice && v4PM) {
      return {
        params: { owner: alice, spender: v4PM, id: "0" },
        label: "Alice's PM allowance for currency-0",
      };
    }
  }

  // V4 StateView
  if (abiKey === "StateView") {
    if (
      (fn.name === "getSlot0" ||
        fn.name === "getLiquidity" ||
        fn.name === "getPoolBitmapInfo") &&
      fn.inputs[0]?.type === "bytes32"
    ) {
      return {
        params: { [fn.inputs[0].name]: ZERO_BYTES32 },
        label: `${fn.name} for pool ID 0`,
      };
    }
  }

  // V4 Position Manager
  if (abiKey === "PositionManager") {
    if (fn.name === "balanceOf" && alice) {
      return { params: { owner: alice }, label: "Alice's V4 positions" };
    }
    if (fn.name === "ownerOf") {
      return { params: { tokenId: "1" }, label: "Owner of V4 position #1" };
    }
    if (fn.name === "getPositionLiquidity") {
      return { params: { tokenId: "1" }, label: "Liquidity in position #1" };
    }
  }

  // ERC-8004 Identity Registry
  if (abiKey === "IdentityRegistry") {
    if (fn.name === "balanceOf" && alice) {
      return { params: { owner: alice }, label: "Alice's identity count" };
    }
    if (fn.name === "ownerOf") {
      return { params: { tokenId: "1" }, label: "Owner of identity #1" };
    }
    if (fn.name === "agentUri") {
      return { params: { tokenId: "1" }, label: "URI for agent #1" };
    }
  }

  // Reputation Registry
  if (abiKey === "ReputationRegistry") {
    if (fn.name === "getScore" && alice) {
      return { params: { agent: alice }, label: "Alice's reputation score" };
    }
    if (fn.name === "getMilestones" && alice) {
      return { params: { agent: alice }, label: "Alice's milestones" };
    }
  }

  // Permit2
  if (abiKey === "Permit2") {
    if (fn.name === "allowance" && alice && weth && v2Router) {
      return {
        params: { owner: alice, token: weth, spender: v2Router },
        label: "Alice's WETH Permit2 allowance to V2 Router",
      };
    }
  }

  // --- Generic fallbacks by parameter pattern ---
  return getGenericSuggestion(fn, knownAddresses);
}

/** Heuristic suggestions for unknown contract functions. */
function getGenericSuggestion(
  fn: AbiFunction,
  knownAddresses: KnownAddress[],
): ExampleSuggestion | null {
  const alice = findAddr(knownAddresses, "Account 1", "Alice");
  if (!alice) return null;

  const params: Record<string, string> = {};
  let meaningful = false;

  for (const input of fn.inputs) {
    const name = input.name.toLowerCase();
    if (input.type === "address") {
      if (
        name.includes("owner") ||
        name.includes("account") ||
        name.includes("user") ||
        name.includes("from") ||
        name === "addr" ||
        name === "who"
      ) {
        params[input.name] = alice;
        meaningful = true;
      } else if (
        name.includes("spender") ||
        name.includes("operator") ||
        name.includes("to") ||
        name.includes("recipient")
      ) {
        const bob = findAddr(knownAddresses, "Account 2", "Bob");
        if (bob) {
          params[input.name] = bob;
          meaningful = true;
        }
      } else {
        params[input.name] = alice;
        meaningful = true;
      }
    } else if (input.type.startsWith("uint") || input.type.startsWith("int")) {
      if (name.includes("amount") || name.includes("value")) {
        params[input.name] = ONE_ETH;
        meaningful = true;
      } else if (name.includes("id") || name.includes("index")) {
        params[input.name] = "1";
        meaningful = true;
      } else if (name === "fee") {
        params[input.name] = "3000";
        meaningful = true;
      } else {
        params[input.name] = "0";
      }
    } else if (input.type === "bool") {
      params[input.name] = "true";
    } else if (input.type === "bytes32" || input.type.startsWith("bytes")) {
      params[input.name] = ZERO_BYTES32.slice(
        0,
        input.type === "bytes32" ? 66 : 2,
      );
    }
  }

  if (!meaningful) return null;

  return {
    params,
    label: `Try with ${fn.inputs.length === 1 ? "Alice" : "example values"}`,
  };
}
